import * as React from 'react';
import { selectorChartPolarCenter } from '../../internals/plugins/featurePlugins/useChartPolarAxis';
import { getChartPoint } from '../../internals/getChartPoint';
import { generateSvg2rotation } from '../../internals/plugins/featurePlugins/useChartPolarAxis/coordinateTransformation';
import { getAxisIndex } from '../../internals/plugins/featurePlugins/useChartPolarAxis/getAxisIndex';
import { useStore } from '../../internals/store/useStore';
import { useChartsLayerContainerRef } from '../../hooks/useChartsLayerContainerRef';
import { useRotationAxis } from '../../hooks/useAxis';

/**
 * This hook provides a function that from pointer event returns the rotation index.
 * @return {(event: { clientX: number; clientY: number }) => number | null} rotationIndexGetter Returns the rotation data index.
 */
export function useRadarRotationIndex() {
  const chartsLayerContainerRef = useChartsLayerContainerRef();

  const store = useStore();
  const rotationAxis = useRotationAxis();

  const center = store.use(selectorChartPolarCenter);

  const rotationIndexGetter = React.useCallback(
    function rotationIndexGetter(event: { clientX: number; clientY: number }) {
      const element = chartsLayerContainerRef.current;
      if (!element || !rotationAxis) {
        // Should never append
        throw new Error(
          `MUI X Charts: The ${!element ? 'SVG element' : 'rotation axis'} was not found. ` +
            'This is required to compute the radar chart dataIndex. ' +
            'Ensure the radar chart is properly initialized with all required axes.',
        );
      }

      const svgPoint = getChartPoint(element, event);
      const rotation = generateSvg2rotation(center)(svgPoint.x, svgPoint.y);
      const rotationIndex = getAxisIndex(rotationAxis, rotation);

      return rotationIndex;
    },
    [center, rotationAxis, chartsLayerContainerRef],
  );
  return rotationIndexGetter;
}
